import React, { useEffect, useMemo, useState } from 'react';
import { LogOut, Play, MessageCircle, TrendingUp, Tag as TagIcon, Clock, UserCheck, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PlayersAPI, EvaluationsAPI, VideosAPI, TagsAPI } from '../api';
import { getCategoriesForPosition, getCategoryScore, getOverallScore } from '../data/criteria';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import VideoPlayer from '../components/VideoPlayer';
import { BrandLogo } from '../components/BrandLogo';

function fmt(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function PlayerView() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [evals, setEvals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    PlayersAPI.list().then(list => {
      setPlayers(list);
      if (list.length) setPlayerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!playerId) return;
    EvaluationsAPI.list(playerId).then(list => setEvals(list.sort((a, b) => new Date(b.date) - new Date(a.date))));
    VideosAPI.list(playerId).then(setVideos);
    TagsAPI.listByPlayer(playerId).then(setTags).catch(() => setTags([]));
  }, [playerId]);

  const player = players.find(p => p.id === playerId);
  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);
  const latest = evals[0];
  const overall = latest ? getOverallScore(latest.ratings, categories) : 0;
  const selfEvals = useMemo(() => evals.filter(e => e.source === 'self'), [evals]);

  const tagsByVideo = useMemo(() => {
    const m = {};
    tags.forEach(t => { (m[t.videoId] = m[t.videoId] || []).push(t); });
    return m;
  }, [tags]);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Mobile-friendly header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <BrandLogo variant="inline" showTagline={false} />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500 hover:text-[#0b1f4d]">
            <LogOut className="h-4 w-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Athlete self-assessment hero — always visible for the player */}
        <div className="bg-gradient-to-br from-[#0b1f4d] to-[#1e40af] text-white rounded-2xl p-5 md:p-6 shadow-sm relative overflow-hidden" data-testid="athlete-self-assessment-hero">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_45%)]" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              <UserCheck className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-1">Your development</div>
              <h2 className="font-brand text-xl md:text-2xl uppercase leading-tight">
                {!player
                  ? 'Start your first self-assessment.'
                  : selfEvals.length === 0
                    ? 'Start your first self-assessment.'
                    : `${selfEvals.length} self-assessment${selfEvals.length === 1 ? '' : 's'} on file. Keep going.`}
              </h2>
              <p className="text-white/80 mt-1.5 text-xs md:text-sm">
                {!player
                  ? "Your coach hasn't linked a player profile to your account yet. Tap below to get started as soon as they do."
                  : 'Rate yourself across the same 10 categories your coach uses. Watch the gap close.'}
              </p>
            </div>
            <Button
              onClick={() => navigate(playerId ? `/app/evaluate/new?player=${playerId}&source=self` : '/app/evaluate/new?source=self')}
              disabled={!player}
              data-testid="start-self-assessment-btn"
              className="bg-white text-[#0b1f4d] hover:bg-slate-100 font-semibold flex-shrink-0 disabled:opacity-70">
              {!player ? 'Waiting for profile' : selfEvals.length === 0 ? 'Start Self-Assessment' : 'Do Another'}
              <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Athlete picker (demo)  in prod this is auto-tied to the account */}
        {players.length > 1 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Viewing</div>
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="bg-white border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                {players.map(p => <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  {p.position}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {player && (
          <>
            {/* Hero */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-4">
                <img src={player.avatar} alt="" className="h-16 w-16 rounded-2xl ring-2 ring-slate-200" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl font-bold text-slate-900">{player.firstName} {player.lastName}</div>
                  <div className="text-sm text-slate-500">#{player.jerseyNumber}  {player.team}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge className={player.position === 'Goalie' ? 'bg-violet-500/10 text-violet-700 border-violet-500/30' : 'bg-sky-500/10 text-sky-600 border-sky-500/30'}>{player.position}</Badge>
                  </div>
                </div>
                {overall > 0 && (
                  <div className="text-right">
                    <div className="font-mono font-bold text-3xl bg-gradient-to-br from-sky-600 to-cyan-600 bg-clip-text text-transparent">{overall}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall</div>
                  </div>
                )}
              </div>
            </div>

            {/* Latest feedback */}
            {latest && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="h-4 w-4 text-sky-600" />
                  <h2 className="font-display font-semibold">Latest Coach Feedback</h2>
                </div>
                <div className="text-xs text-slate-500">{latest.gameContext}  {new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                {latest.notes && <p className="mt-3 text-sm text-slate-700 italic leading-relaxed">{latest.notes}</p>}
                <div className="text-xs text-slate-500 mt-2">  {latest.evaluatorName}</div>

                <div className="mt-4 space-y-2">
                  {categories.map(c => {
                    const v = parseFloat(getCategoryScore(latest.ratings, c.id, categories));
                    return (
                      <div key={c.id}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">{c.name}</span>
                          <span className="font-mono text-sky-600">{v.toFixed(1)}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full" style={{ width: `${(v / 10) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tagged moments */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="h-4 w-4 text-amber-400" />
                <h2 className="font-display font-semibold">Tagged Moments</h2>
                <span className="text-xs text-slate-500">({tags.length})</span>
              </div>
              {tags.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No tagged moments yet. Your coach will drop timestamped notes here.</div>}
              <ul className="space-y-2">
                {tags.slice(0, 10).map(t => {
                  const v = videos.find(x => x.id === t.videoId);
                  const cat = categories.find(c => c.id === t.categoryId);
                  return (
                    <li key={t.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => v && setActiveVideo(v)} disabled={!v}
                          className="flex items-center gap-1.5 font-mono text-xs text-sky-600 bg-sky-500/10 border border-sky-500/30 rounded px-2 py-1 hover:bg-sky-500/20 disabled:opacity-50">
                          <Play className="h-3 w-3" fill="currentColor" /> {fmt(t.timestampSec)}
                        </button>
                        <div className="text-xs text-slate-500 truncate">{v?.title || 'Clip'}</div>
                      </div>
                      <div className="mt-2 text-sm text-slate-900">{t.label}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{t.evaluatorName}</span>
                        {cat && <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px]">{cat.name}</Badge>}
                        {t.rating && <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/30 font-mono text-[10px]">{t.rating}/10</Badge>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Active clip */}
            {activeVideo && (
              <div className="space-y-2">
                <VideoPlayer video={activeVideo} playerId={player.id} playerPosition={player.position} canTag={false} readOnly />
                <div className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setActiveVideo(null)} className="text-slate-500 hover:text-slate-900">Close player</Button>
                </div>
              </div>
            )}

            {/* My clips */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-sky-600" />
                <h2 className="font-display font-semibold">My Clips</h2>
              </div>
              {videos.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Your coach hasn't shared clips yet.</div>}
              <div className="grid grid-cols-2 gap-3">
                {videos.map(v => (
                  <button key={v.id} onClick={() => setActiveVideo(v)}
                    className="text-left bg-slate-50 border border-slate-200 rounded-lg overflow-hidden hover:border-sky-500/40 transition-colors">
                    <div className="relative aspect-video">
                      <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-10 w-10 rounded-full bg-sky-500/90 flex items-center justify-center">
                          <Play className="h-4 w-4 text-slate-900 ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute bottom-1.5 right-1.5 bg-black/70 px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" /> {v.duration}
                      </div>
                      {tagsByVideo[v.id]?.length ? (
                        <div className="absolute top-1.5 left-1.5 bg-amber-400/90 text-slate-900 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded">
                          {tagsByVideo[v.id].length} tags
                        </div>
                      ) : null}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-semibold line-clamp-2">{v.title}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{new Date(v.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="text-center text-[10px] text-slate-400 py-4">
          ShiftNotes<sup className="text-[8px] text-sky-600">&trade;</sup>  a product of NextShift Development LLC
        </div>
      </main>
    </div>
  );
}
